import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SEO from '@/components/common/SEO';
import { GuideCard, ObangsaekStripe } from '@/components/editorial';
import { GUIDES, guidesByCategory } from '@/content/guides/registry';
import { GUIDE_CATEGORIES, categoryVars, type GuideCategory } from '@/content/guides/types';
import {
  ArrowRight,
  Banknote,
  Stamp,
  Users,
  MessageCircle,
  Wallet,
  Globe2,
  BadgeCheck,
  Car,
  FileCheck2,
  Plane,
  type LucideIcon,
} from 'lucide-react';

const CATEGORY_ICONS: Record<string, LucideIcon> = { wallet: Wallet, stamp: Stamp, globe: Globe2 };

const BASE_URL = 'https://koreanhub.net';

/**
 * Hero photograph — the Korean street scene from the original homepage.
 *
 * Recovered from the Cloudflare Pages deployment of commit c402425, which
 * served production for four months but is not in this repository's history.
 * That is why it looked like the image had been pruned: it was never a repo
 * asset and never a tracked line of code — the old markup hotlinked Unsplash
 * through a CSS `backgroundImage`.
 *
 * Now self-hosted from public/ (source: Unsplash photo-1517154421773, used
 * under the Unsplash License). Two reasons that matters: the homepage's
 * largest paint no longer depends on a third-party CDN, and a CSS background
 * cannot be discovered by the preload scanner, whereas this <img> is in the
 * prerendered HTML and carries fetchPriority.
 *
 * Both files are 1600px wide — the hero is a full-bleed band and is never
 * painted wider than the viewport. WebP is served to everything that accepts
 * it (172 KB, down from the 376 KB hotlink) with the JPEG as fallback.
 *
 * `alt` is empty because the hero is decorative; the headline beside it
 * already carries the meaning, and describing the photo would make screen
 * readers announce it twice.
 */
const HERO_IMAGE: { webp: string; jpg: string; alt: string } | null = {
  webp: '/hero-korean-street.webp',
  jpg: '/hero-korean-street.jpg',
  alt: '',
};

/**
 * Homepage — editorial hub.
 *
 * Restructured away from a community feed: the previous version led with three
 * discussion cards and three event cards, which is exactly the "thin content"
 * shape that triggered the AdSense finding. Guides now occupy the space above
 * the fold; community and events are demoted to supporting sections that link
 * out rather than trying to look full.
 */
export default function Home() {
  const { i18n } = useTranslation();
  const isKorean = i18n.language === 'ko';

  const [lead, ...rest] = GUIDES;

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'KoreanHub',
    url: BASE_URL,
    description:
      'Sourced guides for navigating money, visas, and relocation between the United States and Korea.',
    publisher: { '@type': 'Organization', name: 'KoreanHub', url: BASE_URL },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'KoreanHub',
    url: BASE_URL,
    logo: `${BASE_URL}/logo-koreanhub.svg`,
    description: 'Independent editorial guides for people navigating life between the United States and Korea',
  };

  return (
    <div className="bg-white dark:bg-surface-dark">
      <SEO
        title="US–Korea Guides — Money, Visas & Relocation"
        description="Sourced, maintained guides for navigating money transfers, the F-4 visa, and settling in Korea."
        path="/"
        jsonLd={[websiteSchema, organizationSchema]}
      />

      {/* ══ Hero — photo-forward, left-aligned overlay (DESIGN.md) ══ */}
      <section className="relative flex min-h-[380px] items-center overflow-hidden bg-[#1C1C1C] sm:min-h-[440px]">
        {HERO_IMAGE ? (
          <picture>
            <source srcSet={HERO_IMAGE.webp} type="image/webp" />
            <img
              src={HERO_IMAGE.jpg}
              alt={HERO_IMAGE.alt}
              aria-hidden="true"
              // The hero is the LCP element on the homepage. It is preloaded
              // from index.html and must not be lazy-loaded.
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover object-center"
            />
          </picture>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-[#1C1C1C] via-[#2A1519] to-[#0F1E3D]" />
        )}

        {/*
          Dark overlay — the left-aligned scrim DESIGN.md calls for. Over a
          photograph this reproduces the original ramp (95% -> 75% -> 40%),
          which keeps white text well clear of AA on the left while letting the
          photo read on the right. The gradient fallback needs a different
          ramp: it is already dark, so it fades to transparent instead.
        */}
        <div
          className={
            HERO_IMAGE
              ? 'absolute inset-0 bg-gradient-to-r from-[#1C1C1C]/95 via-[#1C1C1C]/75 to-[#1C1C1C]/40'
              : 'absolute inset-0 bg-gradient-to-r from-[#1C1C1C] via-[#1C1C1C]/80 to-transparent'
          }
        />

        <div className="relative z-10 mx-auto w-full max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <div className="max-w-2xl">
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1.5 text-sm text-white/90 backdrop-blur-sm">
              <span lang="ko" className="font-korean">
                미국과 한국 사이의 실용 가이드
              </span>
            </div>

            <h1 className="mb-4 text-3xl font-bold leading-tight text-white sm:text-4xl md:text-5xl">
              {isKorean
                ? '미국과 한국 사이, 복잡한 일을 더 명확하게.'
                : 'Clear answers for life between the US and Korea.'}
            </h1>

            <p className="mb-7 max-w-xl text-base leading-7 text-gray-300 sm:text-lg">
              {isKorean
                ? '송금, F-4 비자, 한국 정착까지 — 1차 출처와 검증 날짜를 공개하는 실용 가이드입니다.'
                : 'Sourced, maintained guides on money transfers, the F-4 visa, and settling in Korea—with primary sources and visible verification dates.'}
            </p>

            <div className="flex flex-wrap gap-3">
              <Link
                to="/guides"
                className="inline-flex items-center gap-2 rounded-lg bg-jeok-500 px-5 py-2.5 font-medium text-white transition-colors hover:bg-[#A00D25]"
              >
                {isKorean ? '가이드 읽기' : 'Read the guides'}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/remittance"
                className="inline-flex items-center gap-2 rounded-lg border border-white/30 px-5 py-2.5 font-medium text-white transition-colors hover:bg-white/10"
              >
                {isKorean ? '환율 비교' : 'Compare USD → KRW rates'}
              </Link>
            </div>

            <p className="mt-6 flex items-center gap-2 text-xs text-white/60">
              <BadgeCheck className="h-3.5 w-3.5" aria-hidden="true" />
              {isKorean
                ? '모든 가이드는 1차 출처를 명시하고 검증 날짜를 공개합니다'
                : 'Every guide cites its primary sources and shows when its figures were last verified'}
            </p>
          </div>
        </div>
      </section>

      <ObangsaekStripe />

      {/* ══ Quick actions — four entry points ══ */}
      <section className="bg-ink-50 px-4 py-6 dark:bg-white/[0.02] sm:px-6">
        <div className="mx-auto grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-4 sm:gap-4">
          {(
            [
              {
                icon: Banknote,
                label: isKorean ? '송금 가이드' : 'Send Money Guides',
                sub: isKorean ? '수수료 · 세금' : 'Fees, rates & tax',
                to: '/guides/sending-money-to-korea',
                color: '#0047AB',
              },
              {
                icon: Stamp,
                label: isKorean ? 'F-4 · 이민' : 'F-4 / Immigration Hub',
                sub: isKorean ? '비자 · 서류' : 'Visas & paperwork',
                to: '/guides/f4-visa-guide',
                color: '#C8102E',
              },
              {
                icon: Plane,
                label: isKorean ? '한국 정착 순서' : 'First Month in Korea',
                sub: isKorean ? '은행 · 휴대폰 · 보험' : 'Banking, phone & NHIS',
                to: '/guides/korea-first-month',
                color: '#FFB81C',
              },
              {
                icon: Car,
                label: isKorean ? '운전면허 교환' : "Driver's Licence",
                sub: isKorean ? '서류 · 시험 · 절차' : 'Documents, tests & steps',
                to: '/guides/korean-drivers-licence-for-expats',
                color: '#1C1C1C',
              },
            ] as const
          ).map((item) => {
            const Icon = item.icon;
            return (
              <Link
                key={item.to}
                to={item.to}
                className="flex flex-col gap-3 rounded-xl border border-ink-200 bg-white p-4 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md dark:border-surface-dark-border dark:bg-surface-dark-card"
              >
                <span
                  className="flex h-10 w-10 items-center justify-center rounded-lg"
                  style={{ backgroundColor: item.color }}
                >
                  <Icon className="h-5 w-5 text-white" aria-hidden="true" />
                </span>
                <span>
                  <span className="block text-sm font-semibold leading-snug text-ink-900 dark:text-ink-50">
                    {item.label}
                  </span>
                  <span className="mt-0.5 block text-xs text-ink-500 dark:text-ink-400">
                    {item.sub}
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ══ Lead story + secondary guides ══ */}
      <section className="px-4 py-10 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <SectionHeader
            title={isKorean ? '주요 가이드' : 'Start here'}
            titleKo={isKorean ? undefined : '주요 가이드'}
            linkText={isKorean ? '전체 가이드 →' : 'All guides →'}
            linkTo="/guides"
          />

          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {lead && <GuideCard guide={lead} featured className="lg:col-span-3" />}
            {rest.map((guide) => (
              <GuideCard key={guide.slug} guide={guide} className="lg:col-span-1" />
            ))}
          </div>
        </div>
      </section>

      {/* ══ Three editorial categories ══ */}
      <section className="bg-ink-50 px-4 py-12 dark:bg-white/[0.02] sm:px-6">
        <div className="mx-auto max-w-5xl">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-ink-900 dark:text-ink-50">
              {isKorean ? '주제별로 찾기' : 'Browse by topic'}
            </h2>
            <p lang="ko" className="mt-0.5 font-korean text-base text-ink-500 dark:text-ink-400">
              주제별 가이드
            </p>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
            {Object.values(GUIDE_CATEGORIES).map((cat) => (
              <CategoryCard key={cat.id} category={cat} />
            ))}
          </div>
        </div>
      </section>

      {/* ══ Reader feedback and maintenance ══ */}
      <section className="px-4 py-12 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white shadow-sm dark:border-surface-dark-border dark:bg-surface-dark-card">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <div className="p-6 sm:p-8">
                <h2 className="text-xl font-bold text-ink-900 dark:text-ink-50">
                  {isKorean ? '가이드에 없는 질문이 있다면' : 'When the guide does not cover it'}
                </h2>
                <p className="mt-3 text-[15px] leading-7 text-ink-700 dark:text-ink-200">
                  {isKorean
                    ? '가이드에서 다루지 못한 상황이 있다면 알려주세요. 비자 심사, 은행 계좌 개설, 첫 정착까지 — 직접 답변드립니다.'
                    : 'Every situation has an edge case. Tell us what the guide missed — the consulate appointment, the bank branch, the first month — and we will answer.'}
                </p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 rounded-lg bg-jeok-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#A00D25] dark:bg-jeok-dark"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {isKorean ? '문의하기' : 'Ask us directly'}
                  </Link>
                  <Link
                    to="/guides/finding-bilingual-mentors"
                    className="inline-flex items-center gap-2 rounded-lg border border-ink-200 px-4 py-2.5 text-sm font-medium text-ink-700 transition-colors hover:bg-ink-50 dark:border-surface-dark-border dark:text-ink-200 dark:hover:bg-white/5"
                  >
                    <Users className="h-4 w-4" />
                    {isKorean ? '커리어 가이드' : 'Career guides'}
                  </Link>
                </div>
              </div>

              <div className="border-t border-ink-200 bg-ink-50 p-6 dark:border-surface-dark-border dark:bg-white/[0.03] sm:p-8 md:border-l md:border-t-0">
                <FileCheck2 className="h-8 w-8 text-cheong-500 dark:text-cheong-dark" aria-hidden="true" />
                <h3 className="mt-4 text-lg font-bold text-ink-900 dark:text-ink-50">
                  {isKorean ? '정보를 최신 상태로 유지합니다' : 'Maintained, not just published'}
                </h3>
                <p className="mt-3 text-sm leading-6 text-ink-600 dark:text-ink-300">
                  {isKorean
                    ? '규정과 수수료는 바뀝니다. 각 가이드에 검증 날짜와 1차 출처를 표시하고, 오류 제보를 직접 검토합니다.'
                    : 'Rules and fees change. Each guide shows its verification date and primary sources, and reader corrections are reviewed directly.'}
                </p>
                <Link
                  to="/about"
                  className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-cheong-500 hover:underline dark:text-cheong-dark"
                >
                  {isKorean ? '편집 원칙 보기' : 'See our editorial approach'}
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ── Category card ── */
function CategoryCard({ category }: { category: GuideCategory }) {
  const Icon = CATEGORY_ICONS[category.icon];
  const guides = guidesByCategory(category.id);

  return (
    <div
      style={categoryVars(category)}
      className="flex flex-col overflow-hidden rounded-xl border border-ink-200 bg-white shadow-sm transition-shadow duration-200 hover:shadow-md dark:border-surface-dark-border dark:bg-surface-dark-card"
    >
      <div className="h-1 w-full bg-[var(--cat-color)]" />

      <div className="flex flex-1 flex-col p-5">
        <span
          className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${category.color}1A` }}
        >
          <Icon
            className="h-5 w-5 text-[var(--cat-text)] dark:text-[var(--cat-text-dark)]"
            aria-hidden="true"
          />
        </span>

        <h3 className="text-base font-bold text-ink-900 dark:text-ink-50">{category.label}</h3>
        <p lang="ko" className="mt-0.5 font-korean text-sm text-ink-500 dark:text-ink-400">
          {category.labelKo}
        </p>
        <p className="mt-2 flex-1 text-sm leading-6 text-ink-500 dark:text-ink-400">
          {category.blurb}
        </p>

        <ul className="mt-4 space-y-2 border-t border-ink-100 pt-4 dark:border-surface-dark-border">
          {guides.map((g) => (
            <li key={g.slug}>
              <Link
                to={`/guides/${g.slug}`}
                className="flex items-start gap-2 text-sm font-medium leading-snug text-ink-700 transition-colors hover:text-cheong-500 dark:text-ink-200 dark:hover:text-cheong-dark"
              >
                <ArrowRight className="mt-1 h-3 w-3 flex-shrink-0 text-ink-400" aria-hidden="true" />
                {g.shortTitle}
              </Link>
            </li>
          ))}
        </ul>

        <Link
          to={`/guides?category=${category.id}`}
          className="mt-4 text-sm font-medium text-[var(--cat-text)] hover:underline dark:text-[var(--cat-text-dark)]"
        >
          See all →
        </Link>
      </div>
    </div>
  );
}

/* ── Section header ── */
function SectionHeader({
  title,
  titleKo,
  linkText,
  linkTo,
}: {
  title: string;
  titleKo?: string;
  linkText: string;
  linkTo: string;
}) {
  return (
    <div className="mb-5 flex items-end justify-between gap-4">
      <div>
        <h2 className="text-2xl font-bold text-ink-900 dark:text-ink-50">{title}</h2>
        {titleKo && (
          <p lang="ko" className="mt-0.5 font-korean text-base text-ink-500 dark:text-ink-400">
            {titleKo}
          </p>
        )}
      </div>
      <Link
        to={linkTo}
        className="flex-shrink-0 text-sm font-medium text-cheong-500 hover:underline dark:text-cheong-dark"
      >
        {linkText}
      </Link>
    </div>
  );
}
